import { hasCurrentUserValidAnnualPass, isFlexPass } from './annualPass';

// Display-only mirror of the server's UPGRADE_DISCOUNT_CENTS
// (server/api/subscription/fulfillUpgrade.js) — the backend coupon is the
// authority for what actually gets discounted.
export const UPGRADE_DISCOUNT_CENTS = 3000;

// The Familie pass covers up to 5 guests, the base JahresPass up to 2. The owned
// code pass records its coverage as included.guestCount (see add-annual-pass.js),
// so anything above the base cap means the holder owns the Familie pass.
export const PASS_BASE_GUEST_LIMIT = 2;

// Pass types that can be bought as a subscription (and so started via `?buy=`).
export const PURCHASABLE_PASS_TYPES = ['annualPass', 'annualPassFamily', '25Pass'];

/**
 * Which subscription pass type the holder of this profile pass may upgrade to:
 * a Familie-pass holder upgrades to the Familie subscription, a base-pass holder
 * to the base one. Never the other variant.
 *
 * @param {import('../../types/vansite').VanSite.Pass|null|undefined} annualPass
 * @returns {'annualPass'|'annualPassFamily'}
 */
export const ownedPassTypeOf = (annualPass) =>
  (annualPass?.included?.guestCount ?? PASS_BASE_GUEST_LIMIT) > PASS_BASE_GUEST_LIMIT
    ? 'annualPassFamily'
    : 'annualPass';

/**
 * True when the user may buy the −30 € upgrade: a still-valid CODE pass with no
 * subscription behind it. The server re-checks all of this in
 * server/api/subscription/upgrade-checkout.js — this only gates the UI.
 *
 * @param {object|null} currentUser
 * @returns {boolean}
 */
export const isPassUpgradeEligible = (currentUser) => {
  const annualPass = currentUser?.attributes?.profile?.privateData?.annualPass;
  return (
    hasCurrentUserValidAnnualPass(currentUser) &&
    !isFlexPass(annualPass) &&
    !annualPass?.subscriptionId &&
    !annualPass?.nextPeriodPrepaidCoupon
  );
};

/**
 * Client mirror of `matchesPassType` in server/api-util/subscriptionPrices.js:
 * a Stripe pass product identifies its type through metadata — `twentyFivePass`
 * flags the 25Pass, the annual passes split by `guestCount` (2 = base, 5 =
 * Familie). Keep in sync with the server.
 *
 * @param {{metadata?: Record<string, string>}} product
 * @returns {'25Pass'|'annualPassFamily'|'annualPass'|null}
 */
export const passTypeOfStripeProduct = (product) => {
  const md = product?.metadata || {};
  if (md.twentyFivePass === 'true') return '25Pass';
  if (md.guestCount === '5') return 'annualPassFamily';
  if (md.guestCount === '2') return 'annualPass';
  return null;
};
